import { cloneDeep } from 'lodash-es';

import activeMission from './activeMission';

/*
* This module includes:
  #1 Tutorial related temporary states.
  #2 TutorialState coming from api.
*/
export default {
  namespaced: false,
  modules: { activeMission },
  state: {
    // #1,
    showingPageNumber: 1,
    // #2 - api
    tutorialState: {
      params: null,
      finished: null,
      currentPage: null,
      tutorialId: null,
      tutorialStateId: null,
      userUuid: null,
      stateUuid: null,
      lastPageStats: {
        startedAt: null,
        finishedAt: null,
        numberOfAttempts: null,
        completed: null
      }
    }
  },
  getters: {
    // #1
    showingPageNumber(state) {
      return state.showingPageNumber;
    },
    nextPageNumber(state) {
      return state.tutorialState.currentPage + 1;
    },
    showingPage(state, getters, rootState, rootGetters) {
      return rootGetters['workspace/tutorial/getPageByPageNumber'](
        getters.showingPageNumber
      );
    },
    isShowingPageTypeDesign(state, getters, rootState, rootGetters) {
      return getters.showingPage.type === 'DESIGN';
    },
    isShowingPageTypeContent(state, getters, rootState, rootGetters) {
      return getters.showingPage.type === 'CONTENT';
    },
    isShowingPageTypeCode(state, getters, rootState, rootGetters) {
      return ['JAVASCRIPT', 'PYTHON'].includes(getters.showingPage.type);
    },
    isShowingPageTypeJavascript(state, getters, rootState, rootGetters) {
      return getters.showingPage.type === 'JAVASCRIPT';
    },
    isShowingPageTypePython(state, getters, rootState, rootGetters) {
      return getters.showingPage.type === 'PYTHON';
    },
    showingPageContent(state, getters) {
      return getters.showingPage?.contents;
    },
    showingPageMissions(state, getters) {
      return getters.showingPage?.missions;
    },
    completedPageNumber(state) {
      return state.tutorialState.currentPage;
    },
    isTutorialFinished(state, getters, rootState, rootGetters) {
      const totalCount = rootGetters['workspace/tutorial/totalPageCount'];
      const solutions = rootState.workspace.session.user.solutions;
      return (
        totalCount === solutions.length && state.tutorialState.finished === true
      );
    },
    isLastPage(state, getters, rootState, rootGetters) {
      const totalCount = rootGetters['workspace/tutorial/totalPageCount'];
      return totalCount === getters.showingPageNumber;
    },
    hasTutorialState(state) {
      return (
        state.tutorialState.userUuid !== null &&
        state.tutorialState.userUuid !== undefined &&
        typeof state.tutorialState.userUuid === 'string'
      );
    },
    currentMissionStats(state, getters, rootState, rootGetters) {
      return {
        startedAt: rootState.workspace.session.tutorial.activeMission.startedAt,
        finishedAt:
          rootState.workspace.session.tutorial.activeMission.finishedAt,
        timeSpent: rootState.workspace.session.tutorial.activeMission.timeSpent,
        numberOfAttempts:
          rootState.workspace.session.tutorial.activeMission.numberOfAttempts,
        completed: state.tutorialState.lastPageStats.completed
      };
    }
  },
  mutations: {
    // #1
    changeShowingPageNumber(state, pageNumber) {
      state.showingPageNumber = pageNumber;
    },
    // #2
    importTutorialState(state, tutorialState) {
      state.tutorialState.params = tutorialState?.params;
      state.tutorialState.userUuid = tutorialState?.userUuid;
      state.tutorialState.stateUuid = tutorialState?.stateUuid;
      state.tutorialState.finished = tutorialState?.finished ?? false;
      state.tutorialState.tutorialId = tutorialState?.tutorialId;
      state.tutorialState.currentPage = tutorialState?.currentPage ?? 0;
      state.tutorialState.tutorialStateId = tutorialState?.tutorialStateId;

      const lastPageStats = JSON.parse(tutorialState.lastPageStats);
      state.tutorialState.lastPageStats = lastPageStats;
    }
  },
  actions: {
    // #1
    prevTutorialPage(action) {
      const { state, commit, rootGetters, rootState } = action;
      if (state.showingPageNumber > 1)
        commit('changeShowingPageNumber', state.showingPageNumber - 1);
    },
    nextTutorialPage(action) {
      const { state, commit, rootGetters, rootState } = action;
      if (
        state.showingPageNumber <
        rootGetters['workspace/tutorial/totalPageCount']
      )
        commit('changeShowingPageNumber', state.showingPageNumber + 1);
    },
    // #2
    fetchTutorialStateByUser(action, tutorialId) {
      const { commit, rootGetters, rootState } = action;
      return rootGetters['CodementumUser/apiRequest']({
        method: 'get',
        path: '/tutorialstates/me',
        body: {
          params: {
            search: 'tutorial.id==' + tutorialId
          }
        },
        opt: {
          baseURL: rootGetters['workspace/mainApi']
        },
        onSuccess(data) {
          if (data?.list?.length > 0)
            commit('importTutorialState', data?.list[0]);
        }
      });
    },
    addTutorialStateByUser(action, missionDone) {
      const { state, commit, getters, rootState, rootGetters } = action;
      let currentPage;

      currentPage = missionDone ? state.showingPageNumber : undefined;

      // currentPage cannot be lower than last solution pagenumber
      if (
        currentPage &&
        rootGetters['workspace/session/lastUserSolutionPageNumber'] &&
        currentPage <
          rootGetters['workspace/session/lastUserSolutionPageNumber']
      ) {
        currentPage =
          rootGetters['workspace/session/lastUserSolutionPageNumber'];
      }

      return rootGetters['CodementumUser/apiRequest']({
        method: 'post',
        path: '/tutorialstates',
        body: {
          params: JSON.stringify(rootGetters['workspace/session/appJson']),
          tutorialId: rootState.workspace.tutorial.tutorialId,
          currentPage,
          lastPageStats: JSON.stringify({
            ...getters.currentMissionStats,
            completed: missionDone
          })
        },
        opt: {
          baseURL: rootGetters['workspace/mainApi']
        },
        onSuccess(data) {
          commit('importTutorialState', data);
        }
      });
    },
    patchTutorialStateByUser(action, missionDone) {
      const { state, commit, getters, rootState, rootGetters } = action;
      let currentPage;

      currentPage = missionDone ? state.showingPageNumber : undefined;

      // currentPage cannot be lower than last solution pagenumber
      if (
        currentPage &&
        rootGetters['workspace/session/lastUserSolutionPageNumber'] &&
        currentPage <
          rootGetters['workspace/session/lastUserSolutionPageNumber']
      ) {
        currentPage =
          rootGetters['workspace/session/lastUserSolutionPageNumber'];
      }

      const appJson = cloneDeep(rootGetters['workspace/session/appJson']);
      appJson.pages = appJson.pages.map((page) => {
        let design = {};
        for (let key in page.design) {
          let obj = page.design[key];
          try {
            obj.values.designElement = null;
          } catch (err) {}
          try {
            obj.designElement.settings.designElement = null;
          } catch (err) {}
          design[key] = obj;
        }
        page.design = design;
        return page;
      });

      return rootGetters['CodementumUser/apiRequest']({
        method: 'patch',
        path: '/tutorialstates/' + state.tutorialState.tutorialStateId,
        body: {
          finished: getters.isTutorialFinished || getters.isLastPage,
          params: JSON.stringify(appJson),
          currentPage,
          lastPageStats: JSON.stringify({
            ...getters.currentMissionStats,
            completed: missionDone
          })
        },
        opt: {
          baseURL: rootGetters['workspace/mainApi']
        },
        onSuccess(data) {
          commit('importTutorialState', data);
        }
      });
    },
    saveTutorialStateByUser(action, missionDone) {
      const { dispatch, state, getters } = action;
      if (getters.hasTutorialState) {
        return dispatch('patchTutorialStateByUser', missionDone);
      } else return dispatch('addTutorialStateByUser', missionDone);
    },
    syncCurrentPageWithTutorialState(action) {
      const { commit, state, rootState, rootGetters } = action;
      const solutions = rootState.workspace.session.user.solutions;

      let openPageNumber = state.tutorialState?.currentPage;
      // check solved;
      if (solutions.length) {
        const isSolved = solutions.find(
          (s) => s.pageNumber === state.tutorialState?.currentPage
        );
        // if solved open next page.
        if (
          isSolved &&
          openPageNumber + 1 <= rootGetters['workspace/tutorial/totalPageCount']
        ) {
          openPageNumber += 1;
        }
      }

      commit('changeShowingPageNumber', openPageNumber);
    }
  }
};
